import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Activity,
  AlertTriangle,
  Archive,
  Ban,
  CheckCircle2,
  Clock3,
  CreditCard,
  ExternalLink,
  GripHorizontal,
  ImageOff,
  Loader2,
  Minus,
  Plus,
  RefreshCw,
  Send,
  ShieldCheck,
  Sparkles,
  TicketCheck,
  UserCheck,
  Users,
  UserX,
  X,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import AdminCastingIngestPanel from './AdminCastingIngestPanel';

const API_URL = window.__BACKEND_URL__;
const AX = { withCredentials: true, timeout: 50000 };
const FALLBACK_HISTORY_TURNS = 8;
const FALLBACK_HISTORY_TURN_CHARS = 1500;
const FALLBACK_HISTORY_TOTAL_CHARS = 6000;
const WIDGET_POSITION_KEY = 'ccuk-admin-ai-widget-position-v1';
const VIEWPORT_GUTTER = 8;
const HEADER_GAP = 8;

const SUGGESTED_QUESTIONS = [
  'What needs my attention first today?',
  'How many profiles are awaiting approval?',
  'How many users are approved and live?',
  'Which area of the platform needs attention?',
  'What should I check before approving a profile?',
  'Draft a warm reply to a support ticket.',
];

function _newSessionId() {
  return `admin-widget-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function _normalisePosition(value) {
  if (!value || !Number.isFinite(value.x) || !Number.isFinite(value.y)) {
    return null;
  }
  return { x: value.x, y: value.y };
}

function _loadStoredPositions() {
  try {
    const stored = JSON.parse(window.localStorage.getItem(WIDGET_POSITION_KEY) || '{}');
    return {
      button: _normalisePosition(stored.button),
      panel: _normalisePosition(stored.panel),
    };
  } catch {
    return { button: null, panel: null };
  }
}

function _headerSafeTop() {
  if (typeof document === 'undefined') return VIEWPORT_GUTTER;

  const header = document.querySelector('[data-testid="header"]');
  if (!header) return VIEWPORT_GUTTER;

  const rect = header.getBoundingClientRect();

  if (!Number.isFinite(rect.bottom) || rect.bottom <= 0) {
    return VIEWPORT_GUTTER;
  }

  return Math.max(
    VIEWPORT_GUTTER,
    Math.ceil(rect.bottom + HEADER_GAP),
  );
}

function _viewportHeight() {
  return window.visualViewport?.height || window.innerHeight;
}

function _viewportWidth() {
  return window.visualViewport?.width || window.innerWidth;
}

function _applyPanelViewportLimit(element) {
  if (!element) return;

  const bottomGap = window.innerWidth >= 640 ? 24 : 16;
  const availableHeight = (
    _viewportHeight()
    - _headerSafeTop()
    - bottomGap
  );

  element.style.maxHeight = `${Math.max(
    120,
    Math.floor(availableHeight),
  )}px`;
}

function _clampPosition(position, element) {
  if (!position || !element) return position;

  const minY = _headerSafeTop();

  const maxX = Math.max(
    VIEWPORT_GUTTER,
    _viewportWidth() - element.offsetWidth - VIEWPORT_GUTTER,
  );

  const maxY = Math.max(
    minY,
    _viewportHeight() - element.offsetHeight - VIEWPORT_GUTTER,
  );

  return {
    x: Math.min(
      Math.max(position.x, VIEWPORT_GUTTER),
      maxX,
    ),
    y: Math.min(
      Math.max(position.y, minY),
      maxY,
    ),
  };
}

function _positionStyle(position) {
  if (!position) return undefined;
  return {
    left: `${position.x}px`,
    top: `${position.y}px`,
    right: 'auto',
    bottom: 'auto',
  };
}

// Return only a broad allow-listed product area. Never send a raw URL,
// record id, query string, email address or page contents automatically.
function _safePageContext() {
  const path = window.location?.pathname || '';
  if (path.startsWith('/admin/users')) return 'User management';
  if (path.startsWith('/admin/casting') || path.startsWith('/castings/manage')) {
    return 'Casting management';
  }
  if (path.startsWith('/admin')) return 'Admin dashboard';
  if (path.startsWith('/applications')) return 'Applications';
  if (path.startsWith('/messages')) return 'Messages';
  if (path.startsWith('/planner')) return 'Planner';
  if (path.startsWith('/support')) return 'Support Centre';
  if (path.startsWith('/audition')) return 'Audition Rooms';
  if (path.startsWith('/account') || path.startsWith('/settings')) {
    return 'Account settings';
  }
  if (
    path.startsWith('/talent')
    || path.startsWith('/agents')
    || path.startsWith('/crew')
    || path.startsWith('/companies')
    || path.startsWith('/casting-professionals')
    || path.startsWith('/industry-services')
  ) {
    return 'Public directories';
  }
  return 'General platform';
}

function formatRefreshTime(value) {
  if (!value) return 'Not refreshed';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Refresh time unavailable';
  return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

function dayPart() {
  const hour = new Date().getHours();
  if (hour < 12) return 'morning';
  if (hour < 18) return 'afternoon';
  return 'evening';
}

function SummaryMetric({ metricKey, metric, icon: Icon }) {
  if (!metric) return null;
  const value = metric.value == null ? '—' : metric.value;
  return (
    <a
      href={metric.href}
      className="group rounded-xl border border-theme-border-subtle bg-theme-bg-secondary/70 p-2.5 hover:border-[#002FA7]/35 hover:bg-[#002FA7]/[0.04] transition-colors"
      data-testid={`admin-ai-summary-${metricKey}`}
      title={metric.description}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="h-7 w-7 rounded-lg bg-[#002FA7]/10 text-[#002FA7] dark:text-[#7AA2FF] flex items-center justify-center flex-shrink-0">
          <Icon className="h-3.5 w-3.5" />
        </span>
        <ExternalLink className="h-3 w-3 text-theme-muted opacity-50 group-hover:opacity-100" />
      </div>
      <div className="mt-2 text-xl font-bold leading-none text-theme-text">{value}</div>
      <div className="mt-1 text-[10.5px] font-semibold leading-tight text-theme-text-secondary">
        {metric.label}
      </div>
    </a>
  );
}

function StatusMetric({ statusKey, status, icon: Icon, wide = false }) {
  if (!status) return null;
  const value = status.value == null ? '—' : status.value;
  return (
    <a
      href={status.href}
      className={`group rounded-lg border border-theme-border-subtle bg-theme-surface px-2.5 py-2 hover:border-[#002FA7]/35 hover:bg-[#002FA7]/[0.04] transition-colors ${
        wide ? 'col-span-2' : ''
      }`}
      data-testid={`admin-ai-status-${statusKey}`}
      title={`Open ${status.label.toLowerCase()} users`}
    >
      <div className="flex items-center gap-2">
        <span className="h-6 w-6 rounded-md bg-theme-bg-secondary text-[#002FA7] dark:text-[#7AA2FF] flex items-center justify-center flex-shrink-0">
          <Icon className="h-3 w-3" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] text-theme-muted leading-none">{status.label}</div>
          <div className="text-sm font-bold text-theme-text mt-1 leading-none">{value}</div>
        </div>
        <ExternalLink className="h-3 w-3 text-theme-muted opacity-40 group-hover:opacity-100" />
      </div>
    </a>
  );
}

export default function AdminAIAssistantWidget() {
  const { user, userRoles, activeRole } = useAuth();

  // Admin AI follows the account's permanent admin capability, NOT the
  // currently selected testing role. This lets Wayne switch into Talent,
  // Agent, Casting Professional, Production Company, etc. and keep the
  // private AI co-pilot available while testing those experiences.
  //
  // Site Management navigation is deliberately separate and remains visible
  // only when active_role === 'admin'.
  const isAdmin = !!(
    user && (
      user.role === 'admin'
      || activeRole === 'admin'
      || userRoles?.includes?.('admin')
      || user.is_master_admin === true
      || user.is_staff === true
    )
  );

  const storedPositionsRef = useRef(_loadStoredPositions());
  const [buttonPosition, setButtonPosition] = useState(storedPositionsRef.current.button);
  const [panelPosition, setPanelPosition] = useState(storedPositionsRef.current.panel);
  const [isOpen, setIsOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [castingOpen, setCastingOpen] = useState(false);
  const [config, setConfig] = useState(null);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState('');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const sessionIdRef = useRef(_newSessionId());
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);
  const positionsRef = useRef({
    button: storedPositionsRef.current.button,
    panel: storedPositionsRef.current.panel,
  });
  const dragRef = useRef(null);
  const suppressButtonClickRef = useRef(false);

  const applyPosition = useCallback((kind, nextPosition) => {
    positionsRef.current = {
      ...positionsRef.current,
      [kind]: nextPosition,
    };
    if (kind === 'button') setButtonPosition(nextPosition);
    else setPanelPosition(nextPosition);
  }, []);

  const persistPositions = useCallback(() => {
    try {
      window.localStorage.setItem(
        WIDGET_POSITION_KEY,
        JSON.stringify(positionsRef.current),
      );
    } catch {
      // Dragging still works when browser storage is unavailable.
    }
  }, []);

  const clampVisiblePositions = useCallback(() => {
    const clampElement = (kind, element) => {
      if (!element) return;

      if (kind === 'panel') {
        _applyPanelViewportLimit(element);
      }

      const rect = element.getBoundingClientRect();

      const current = positionsRef.current[kind] || {
        x: rect.left,
        y: rect.top,
      };

      const next = _clampPosition(current, element);

      if (!next) return;

      const previous = positionsRef.current[kind];

      if (
        !previous
        || next.x !== previous.x
        || next.y !== previous.y
      ) {
        applyPosition(kind, next);
      }
    };

    clampElement('button', buttonRef.current);
    clampElement('panel', panelRef.current);
  }, [applyPosition]);

  const loadSummary = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setSummaryLoading(true);
    setSummaryError('');
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/ai/summary`, AX);
      setSummary(data);
    } catch (error) {
      const status = error?.response?.status;
      setSummaryError(
        status === 401 || status === 403
          ? 'Admin intelligence is not authorised for this session.'
          : 'Live admin information is temporarily unavailable.',
      );
    } finally {
      if (!silent) setSummaryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAdmin || !isOpen || config) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/admin/ai/config`, AX);
        if (!cancelled) setConfig(data);
      } catch {
        // Non-fatal: the assistant can still display the read-only summary.
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, isAdmin, config]);

  useEffect(() => {
    if (!isAdmin || !isOpen || summary || summaryLoading) return;
    loadSummary();
  }, [isAdmin, isOpen, summary, summaryLoading, loadSummary]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, busy]);

  useEffect(() => {
    const onResize = () => {
      clampVisiblePositions();
      persistPositions();
    };

    const frame = window.requestAnimationFrame(
      clampVisiblePositions,
    );

    const visualViewport = window.visualViewport;

    window.addEventListener('resize', onResize);
    visualViewport?.addEventListener('resize', onResize);
    visualViewport?.addEventListener('scroll', onResize);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', onResize);
      visualViewport?.removeEventListener('resize', onResize);
      visualViewport?.removeEventListener('scroll', onResize);
    };
  }, [clampVisiblePositions, persistPositions]);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      clampVisiblePositions();
      persistPositions();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [isOpen, minimized, clampVisiblePositions, persistPositions]);

  if (!isAdmin) return null;

  const startDrag = (kind, event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (kind === 'panel' && event.target.closest('button, a, textarea, input')) return;

    const element = kind === 'button' ? buttonRef.current : panelRef.current;
    if (!element) return;
    const rect = element.getBoundingClientRect();
    dragRef.current = {
      kind,
      pointerId: event.pointerId,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
      startX: event.clientX,
      startY: event.clientY,
      width: rect.width,
      height: rect.height,
      moved: false,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const continueDrag = (event) => {
    const drag = dragRef.current;

    if (!drag || drag.pointerId !== event.pointerId) return;

    const distance = Math.hypot(
      event.clientX - drag.startX,
      event.clientY - drag.startY,
    );

    if (!drag.moved && distance < 4) return;

    drag.moved = true;

    const element = (
      drag.kind === 'button'
        ? buttonRef.current
        : panelRef.current
    );

    if (drag.kind === 'panel') {
      _applyPanelViewportLimit(element);
    }

    const nextPosition = _clampPosition(
      {
        x: event.clientX - drag.offsetX,
        y: event.clientY - drag.offsetY,
      },
      element,
    );

    if (nextPosition) {
      applyPosition(drag.kind, nextPosition);
    }

    event.preventDefault();
  };

  const finishDrag = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    if (drag.kind === 'button' && drag.moved) {
      suppressButtonClickRef.current = true;
      window.setTimeout(() => { suppressButtonClickRef.current = false; }, 0);
    }
    try {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    } catch {
      // Pointer capture may already have been released by the browser.
    }
    dragRef.current = null;
    persistPositions();
  };

  const startNewChat = () => {
    if (busy) return;
    setMessages([]);
    setInput('');
    sessionIdRef.current = _newSessionId();
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const send = async (suggestedText = null) => {
    const candidate = typeof suggestedText === 'string' ? suggestedText : input;
    const text = candidate.trim();
    if (!text || busy) return;

    const maxTurns = config?.max_history_turns || FALLBACK_HISTORY_TURNS;
    const maxTurnChars = (
      config?.max_history_turn_chars || FALLBACK_HISTORY_TURN_CHARS
    );
    let remainingHistoryChars = (
      config?.max_history_total_chars || FALLBACK_HISTORY_TOTAL_CHARS
    );
    const eligibleHistory = messages
      .filter((message) => message.role === 'user' || message.role === 'assistant')
      .slice(-maxTurns);
    const history = [];
    for (let index = eligibleHistory.length - 1; index >= 0; index -= 1) {
      if (remainingHistoryChars <= 0) break;
      const message = eligibleHistory[index];
      const textForContext = String(message.text || '').slice(
        0,
        Math.min(maxTurnChars, remainingHistoryChars),
      );
      if (textForContext) {
        history.unshift({ role: message.role, text: textForContext });
        remainingHistoryChars -= textForContext.length;
      }
    }

    setInput('');
    setMessages((current) => [...current, { role: 'user', text, ts: Date.now() }]);
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/ai/chat`,
        {
          prompt: text,
          session_id: sessionIdRef.current,
          model: config?.fast_model || 'gpt-4.1-mini',
          history,
          page_context: _safePageContext(),
        },
        AX,
      );
      setMessages((current) => [
        ...current,
        {
          role: 'assistant',
          text: data.output || '',
          ts: Date.now(),
          model: data.model,
          latency_ms: data.latency_ms,
          history_turns_used: data.history_turns_used,
          grounding: data.grounding,
        },
      ]);
      if (data.grounding?.mode === 'live_admin_summary') {
        loadSummary({ silent: true });
      }
    } catch (error) {
      const status = error?.response?.status;
      const raw = error?.response?.data?.detail || error.message || 'Unknown error';
      let clean = raw;
      if (status === 401 || status === 403) clean = 'Not authorised — admin only.';
      else if (status === 429) clean = 'Rate limited. Try again in a moment.';
      else if (status === 424) clean = `Upstream model error: ${String(raw).slice(0, 240)}`;
      else if (status === 503) clean = 'AI or live admin intelligence is not configured on this server.';
      else if (status === 504 || error?.code === 'ECONNABORTED') clean = 'The AI took too long to answer. Try a shorter question.';
      else if (!status) clean = 'Network error — check your connection.';
      setMessages((current) => [
        ...current,
        { role: 'error', text: clean, ts: Date.now(), status },
      ]);
      if (status === 429) toast.warning('AI rate limit reached');
      else if (status !== 401 && status !== 403) toast.error('AI request failed');
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      send();
    }
  };

  if (!isOpen || minimized) {
    return (
      <button
        ref={buttonRef}
        type="button"
        onPointerDown={(event) => startDrag('button', event)}
        onPointerMove={continueDrag}
        onPointerUp={finishDrag}
        onPointerCancel={finishDrag}
        onClick={() => {
          if (suppressButtonClickRef.current) return;
          setIsOpen(true);
          setMinimized(false);
        }}
        className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-[125] h-12 w-12 sm:h-14 sm:w-14 rounded-full bg-[#002FA7] hover:bg-[#0055FF] text-white shadow-xl flex items-center justify-center transition-transform hover:scale-105 border-2 border-white/20 cursor-grab active:cursor-grabbing touch-none"
        style={_positionStyle(buttonPosition)}
        aria-label="Open Admin AI Assistant"
        data-testid="admin-ai-widget-button"
        title="Drag to move · click to open Admin AI Assistant"
      >
        <Sparkles className="h-5 w-5 sm:h-6 sm:w-6" />
      </button>
    );
  }

  const firstName = (user?.name || 'Wayne').trim().split(/\s+/)[0] || 'Wayne';
  const metrics = summary?.metrics || {};
  const userStatuses = summary?.user_statuses || {};

  return (
    <>
    <div
      ref={panelRef}
      className="fixed z-[130] bg-theme-surface border border-theme rounded-xl shadow-2xl flex flex-col
                 bottom-4 right-4 sm:bottom-6 sm:right-6
                 w-[calc(100vw-2rem)] sm:w-[440px] max-w-[480px]
                 h-[76dvh] sm:h-[620px] max-h-[calc(100dvh-6rem)] sm:max-h-[calc(100dvh-6.5rem)]"
      style={_positionStyle(panelPosition)}
      role="dialog"
      aria-label="Admin AI Assistant"
      data-testid="admin-ai-widget-panel"
    >
      <div
        className="flex items-center gap-2 px-3 py-2 border-b border-theme bg-[#002FA7]/5 rounded-t-xl cursor-move active:cursor-grabbing select-none touch-none"
        onPointerDown={(event) => startDrag('panel', event)}
        onPointerMove={continueDrag}
        onPointerUp={finishDrag}
        onPointerCancel={finishDrag}
        data-testid="admin-ai-widget-drag-handle"
        title="Drag to move the Admin AI Assistant"
      >
        <div className="h-7 w-7 rounded-full bg-[#002FA7] text-white flex items-center justify-center flex-shrink-0">
          <Sparkles className="h-3.5 w-3.5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-theme-text truncate flex items-center gap-1.5">
            <span>CCUK Admin Co-pilot</span>
            <GripHorizontal className="h-3.5 w-3.5 text-theme-muted flex-shrink-0" />
          </div>
          <div className="text-[10px] text-theme-muted truncate flex items-center gap-1">
            {config?.key_configured ? (
              <><ShieldCheck className="h-2.5 w-2.5 text-emerald-500" /> {config?.default_model || 'openai'} · grounded · read-only</>
            ) : (
              <>Admin-only intelligence, guidance and drafting.</>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={() => setCastingOpen(true)}
          className="h-7 px-2 rounded-lg border border-[#002FA7]/30 bg-[#002FA7]/[0.06] text-[10.5px] font-semibold text-[#002FA7] hover:bg-[#002FA7]/[0.12] flex items-center gap-1 transition-colors whitespace-nowrap"
          aria-label="Create casting call with AI"
          data-testid="admin-ai-casting-option"
          title="Create a casting call from text and a flyer"
        >
          AI Casting
        </button>
        <button
          type="button"
          onClick={startNewChat}
          className="h-7 px-2 rounded-lg border border-theme text-[10.5px] font-semibold text-theme-text-secondary hover:text-[#002FA7] hover:bg-[#002FA7]/[0.06] flex items-center gap-1 transition-colors"
          aria-label="Start a new admin chat"
          data-testid="admin-ai-widget-new-chat"
          title="Clear this session and start a new chat"
        >
          <Plus className="h-3 w-3" />
          New chat
        </button>
        <button
          type="button"
          onClick={() => setMinimized(true)}
          className="text-theme-muted hover:text-theme-text p-1 rounded"
          aria-label="Minimise Admin AI Assistant"
          data-testid="admin-ai-widget-minimise"
          title="Minimise"
        >
          <Minus className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => setIsOpen(false)}
          className="text-theme-muted hover:text-theme-text p-1 rounded"
          aria-label="Close Admin AI Assistant"
          data-testid="admin-ai-widget-close"
          title="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-3 py-3 space-y-3"
        data-testid="admin-ai-widget-messages"
      >
        {messages.length === 0 && (
          <div data-testid="admin-ai-summary" className="space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-bold text-theme-text">
                  Good {dayPart()}, {firstName}
                </div>
                <div className="text-[11px] text-theme-muted mt-0.5">
                  Here is the current read-only CCUK admin picture.
                </div>
              </div>
              <button
                type="button"
                onClick={() => loadSummary()}
                disabled={summaryLoading}
                className="h-7 px-2 rounded-lg text-[10.5px] font-semibold text-theme-muted hover:text-[#002FA7] hover:bg-[#002FA7]/[0.06] flex items-center gap-1 disabled:opacity-50"
                data-testid="admin-ai-summary-refresh"
                title="Refresh live admin information"
              >
                <RefreshCw className={`h-3 w-3 ${summaryLoading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>

            {summaryLoading && !summary && (
              <div className="rounded-xl border border-theme-border-subtle bg-theme-bg-secondary p-4 text-[11px] text-theme-muted flex items-center gap-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Checking approvals, support and account health…
              </div>
            )}

            {summaryError && !summary && (
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 text-[11px] text-amber-600 dark:text-amber-300 flex items-start gap-2">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                <span>{summaryError} The chat will clearly flag anything it cannot verify.</span>
              </div>
            )}

            {summary && (
              <>
                <a
                  href={summary.recommendation?.href || '/dashboard?tab=overview'}
                  className="block rounded-xl border border-[#002FA7]/20 bg-gradient-to-br from-[#002FA7]/10 to-[#7A1DD8]/[0.06] p-3 hover:border-[#002FA7]/40 transition-colors"
                  data-testid="admin-ai-summary-recommendation"
                >
                  <div className="flex items-start gap-2.5">
                    <span className="h-8 w-8 rounded-lg bg-[#002FA7] text-white flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 className="h-4 w-4" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-[#002FA7] dark:text-[#7AA2FF]">
                        Recommended first action
                      </div>
                      <div className="text-[12px] font-bold text-theme-text mt-0.5">
                        {summary.recommendation?.title}
                      </div>
                      <div className="text-[10.5px] text-theme-muted mt-1">
                        {summary.recommendation?.reason}
                      </div>
                    </div>
                    <ExternalLink className="h-3.5 w-3.5 text-theme-muted flex-shrink-0" />
                  </div>
                </a>

                <div className="grid grid-cols-2 gap-2">
                  <SummaryMetric metricKey="pending-approvals" metric={metrics.pending_approvals} icon={Users} />
                  <SummaryMetric metricKey="pending-missing-image" metric={metrics.pending_missing_image} icon={ImageOff} />
                  <SummaryMetric metricKey="support-waiting" metric={metrics.support_waiting} icon={TicketCheck} />
                  <SummaryMetric metricKey="failed-payments" metric={metrics.failed_payments} icon={CreditCard} />
                </div>

                <div
                  className="rounded-xl border border-theme-border-subtle bg-theme-bg-secondary/40 p-2.5"
                  data-testid="admin-ai-user-statuses"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-wider text-theme-muted">
                        User status
                      </div>
                      <div className="text-[9.5px] text-theme-muted mt-0.5">
                        Verified aggregate totals only
                      </div>
                    </div>
                    <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" />
                  </div>
                  <div className="grid grid-cols-2 gap-1.5">
                    <StatusMetric statusKey="total-visible" status={userStatuses.total_visible} icon={Users} wide />
                    <StatusMetric statusKey="approved-live" status={userStatuses.approved_live} icon={UserCheck} />
                    <StatusMetric statusKey="suspended" status={userStatuses.suspended} icon={Ban} />
                    <StatusMetric statusKey="rejected" status={userStatuses.rejected} icon={UserX} />
                    <StatusMetric statusKey="archived" status={userStatuses.archived} icon={Archive} />
                  </div>
                </div>

                <a
                  href={summary.activity_today?.href || '/dashboard?tab=analytics'}
                  className="rounded-xl border border-theme-border-subtle bg-theme-bg-secondary/50 p-2.5 flex items-center gap-3 hover:border-[#002FA7]/30 transition-colors"
                  data-testid="admin-ai-summary-activity"
                >
                  <span className="h-8 w-8 rounded-lg bg-theme-surface border border-theme flex items-center justify-center text-[#002FA7] flex-shrink-0">
                    <Activity className="h-4 w-4" />
                  </span>
                  <div className="grid grid-cols-3 gap-3 flex-1 text-center">
                    <div>
                      <div className="text-sm font-bold text-theme-text">{summary.activity_today?.registrations ?? 0}</div>
                      <div className="text-[9.5px] text-theme-muted">Registrations</div>
                    </div>
                    <div>
                      <div className="text-sm font-bold text-theme-text">{summary.activity_today?.published_casting_calls ?? 0}</div>
                      <div className="text-[9.5px] text-theme-muted">Castings</div>
                    </div>
                    <div>
                      <div className="text-sm font-bold text-theme-text">{summary.activity_today?.support_tickets_created ?? 0}</div>
                      <div className="text-[9.5px] text-theme-muted">New tickets</div>
                    </div>
                  </div>
                  <ExternalLink className="h-3 w-3 text-theme-muted" />
                </a>

                <div className="flex items-center justify-between text-[9.5px] text-theme-muted px-0.5">
                  <span className="flex items-center gap-1">
                    <ShieldCheck className="h-3 w-3 text-emerald-500" />
                    Live CCUK data · read-only
                  </span>
                  <span>Refreshed {formatRefreshTime(summary.computed_at)}</span>
                </div>
              </>
            )}

            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-theme-muted mb-2">
                Ask the co-pilot
              </div>
              <div className="flex flex-wrap gap-1.5">
                {SUGGESTED_QUESTIONS.map((question) => (
                  <button
                    key={question}
                    type="button"
                    onClick={() => send(question)}
                    disabled={busy}
                    className="rounded-full border border-theme px-2.5 py-1.5 text-[10.5px] text-theme-text-secondary hover:text-[#002FA7] hover:border-[#002FA7]/35 hover:bg-[#002FA7]/[0.05] disabled:opacity-50 transition-colors"
                    data-testid="admin-ai-suggested-question"
                  >
                    {question}
                  </button>
                ))}
              </div>
            </div>

            <div className="rounded-lg bg-theme-bg-secondary/50 px-2.5 py-2 text-[9.5px] leading-relaxed text-theme-muted flex items-start gap-1.5">
              <Clock3 className="h-3 w-3 mt-0.5 flex-shrink-0" />
              <span>
                The co-pilot can read approved aggregate data and CCUK rules. It cannot approve, suspend,
                message, delete or alter an account. Individual contact details remain in protected Admin Users records.
              </span>
            </div>
          </div>
        )}

        {messages.map((message, index) => {
          if (message.role === 'error') {
            return (
              <div
                key={`${message.ts}-${index}`}
                className="rounded-lg border border-rose-500/30 bg-rose-500/5 p-2 text-[11px] text-rose-400 flex items-start gap-1.5"
                data-testid={`admin-ai-widget-msg-error-${index}`}
              >
                <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                <span className="break-words">
                  {message.status ? `[HTTP ${message.status}] ` : ''}{message.text}
                </span>
              </div>
            );
          }
          const isUser = message.role === 'user';
          return (
            <div
              key={`${message.ts}-${index}`}
              className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}
              data-testid={`admin-ai-widget-msg-${message.role}-${index}`}
            >
              <div
                className={`max-w-[88%] rounded-lg px-3 py-2 text-[12px] whitespace-pre-wrap break-words ${
                  isUser
                    ? 'bg-[#002FA7] text-white'
                    : 'bg-theme-bg-secondary text-theme-text border border-theme-border-subtle'
                }`}
              >
                {message.text}
                {!isUser && (
                  <div className="mt-1.5 pt-1.5 border-t border-theme-border-subtle/70 flex flex-wrap items-center gap-x-2 gap-y-1 text-[9.5px] text-theme-muted opacity-80">
                    {message.grounding?.mode === 'live_admin_summary' ? (
                      <span className="flex items-center gap-1">
                        <ShieldCheck className="h-2.5 w-2.5 text-emerald-500" />
                        Grounded in live CCUK summary
                      </span>
                    ) : (
                      <span className="flex items-center gap-1">
                        <AlertTriangle className="h-2.5 w-2.5 text-amber-500" />
                        Live grounding unavailable
                      </span>
                    )}
                    {message.latency_ms != null && <span>{message.model} · {message.latency_ms}ms</span>}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {busy && (
          <div className="flex justify-start" data-testid="admin-ai-widget-loading">
            <div className="bg-theme-bg-secondary text-theme-muted border border-theme-border-subtle rounded-lg px-3 py-2 text-[12px] flex items-center gap-2">
              <Loader2 className="h-3 w-3 animate-spin" />
              Checking CCUK information…
            </div>
          </div>
        )}
      </div>

      <div className="border-t border-theme p-2 flex items-end gap-2">
        <textarea
          ref={inputRef}
          value={input}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Ask about approvals, support, users or today’s activity…"
          rows={2}
          disabled={busy}
          className="flex-1 rounded-lg border border-theme bg-theme-bg-secondary text-theme-text text-[12px] p-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7] resize-none"
          data-testid="admin-ai-widget-input"
        />
        <button
          type="button"
          onClick={() => send()}
          disabled={busy || !input.trim()}
          className="h-8 w-8 rounded-lg bg-[#002FA7] hover:bg-[#0055FF] disabled:opacity-40 disabled:cursor-not-allowed text-white flex items-center justify-center flex-shrink-0"
          aria-label="Send"
          data-testid="admin-ai-widget-send"
          title="Send question"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </button>
      </div>
    </div>

    {castingOpen && (
      <div
        className="fixed inset-0 z-[190] bg-black/35 p-2 sm:p-5 flex items-stretch sm:items-center justify-center"
        data-testid="admin-ai-casting-overlay"
        role="dialog"
        data-ccuk-mobile-dialog-root="true"
        aria-modal="true"
        aria-label="AI casting intake"
      >
        <div className="relative w-full sm:w-[500px] h-[calc(100dvh-1rem)] sm:h-[min(760px,calc(100dvh-2.5rem))] rounded-xl border border-theme bg-theme-surface shadow-2xl overflow-hidden">
          <button
            type="button"
            onClick={() => setCastingOpen(false)}
            className="absolute top-2.5 right-2.5 z-10 h-8 w-8 rounded-lg border border-theme bg-theme-surface text-theme-muted hover:text-theme-text flex items-center justify-center"
            aria-label="Close AI casting intake"
            title="Close"
          >
            <X className="h-4 w-4" />
          </button>
          <AdminCastingIngestPanel onClose={() => setCastingOpen(false)} />
        </div>
      </div>
    )}
  </>
  );
}
